import clsx from 'clsx'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageMultiRoomMode } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { getMultiRoomPackagesForFilters, getSelectedOfferPageMultiRoomDay } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import Group from 'components/utils/Group'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferCalenderDatePickerFooterPriceDetails from '../CalendarPicker/OfferCalenderDatePickerFooterPriceDetails'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import { canBookNow, getDurationOptions, getPackagesForFilters, getSelectedFixedFlashLengthOfStayOfferPageDay, getSelectedOfferPageDay, getSelectedPackageView } from 'selectors/offerPage/offerPageSelectors'
import { getFirstAvailableDuration } from 'components/OfferPage/FlashHotel/flashHotelCalendarSkipUtils'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import eases from 'styles/tools/eases'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_VILLA } from 'constants/offer'
import OfferAddToCartButton from 'components/OfferPage/OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from 'components/OfferPage/OfferBookButtons/OfferSelectPackageButton'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { getFixedFlashFooterSelectedDay } from '../CalendarPicker/fixedFlashLengthOfStayCalendarUtils'

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  inset-inline: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

const MobileButtonGroup = styled(Group)`
  & > * {
    flex: 1 1 0;
    min-width: 0;
  }
`

interface Props {
  offer: App.Offer
  selectedDay?: App.CalendarDay
  fixedFlashLengthOfStaySelectedDay?: App.CalendarDay
  checkIn?: string
  checkOut?: string
  duration: number
  isAnyFlightsActive?: boolean
  relevantPackages: Array<App.OfferPackageOptionView>
  selectedPackageView?: App.OfferPackageOptionView
  bookable?: boolean
  mobileMultiRoomMode: OfferPageMultiRoomMode | undefined
  hideContinue?: boolean
  isFixedFlashLengthOfStayEnabled?: boolean
  minimumDuration?: number
}

function OfferCalendarSidebarDatePickerFooter(props: Props) {
  const {
    offer,
    selectedDay,
    fixedFlashLengthOfStaySelectedDay,
    checkIn,
    checkOut,
    duration,
    isAnyFlightsActive,
    relevantPackages,
    selectedPackageView,
    bookable,
    mobileMultiRoomMode,
    hideContinue,
    isFixedFlashLengthOfStayEnabled = false,
    minimumDuration,
  } = props

  const intl = useIntl()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const useNewMobileHotelOfferPage = isMobileScreen && offer.type === OFFER_TYPE_HOTEL
  const useFixedFlashMobileFooter = useNewMobileHotelOfferPage && isFixedFlashLengthOfStayEnabled

  const clearDatesLabel = intl.formatMessage({
    id: 'RT3Wp6Ylio7DP0kOaccf',
    defaultMessage: 'Clear dates',
  })
  const resetDatesLabel = intl.formatMessage({
    id: 'X3hrJC6msgo52ySECemL',
    defaultMessage: 'Reset',
  })
  const resetButtonLabel = useFixedFlashMobileFooter ? clearDatesLabel : resetDatesLabel

  const footerSelectedDay = useMemo(
    () => getFixedFlashFooterSelectedDay({
      isFixedFlashLengthOfStayEnabled,
      checkIn,
      checkOut,
      selectedDay,
      fixedFlashLengthOfStaySelectedDay,
    }),
    [checkIn, checkOut, fixedFlashLengthOfStaySelectedDay, isFixedFlashLengthOfStayEnabled, selectedDay],
  )

  // We want to default to continue for any multi room mode purchase to re-confirm the selection
  const hasDateSelected = !!footerSelectedDay && footerSelectedDay.hotelAvailable
  const canBookNow = hasDateSelected && !mobileMultiRoomMode && bookable
  const showClearDatesOnly = useFixedFlashMobileFooter && !!checkIn && !checkOut
  const shouldShowFooter = useFixedFlashMobileFooter ?
      !!checkIn :
      !!footerSelectedDay

  // Store the selected day locally and persist it until the footer is hidden
  // otherwise we get janky animation when the day disappears and we have no information
  // to show in the footer
  const [localDay, setLocalDay] = useState<App.CalendarDay | undefined>(footerSelectedDay)
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()
  const intlDateFormatter = useIntlDateFormatter()

  const canAddToUserCart = canBookNow && offer.type !== OFFER_TYPE_VILLA
  const onBookNowButtonClick = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.BookNow)
  }, [onUserAnalyticsAction])

  const onContinue = useCallback(() => {
    dispatch({ type: OfferPageActions.filterContinue })
  }, [dispatch])

  const onResetDates = useCallback(() => {
    if (isFixedFlashLengthOfStayEnabled && minimumDuration !== undefined) {
      dispatch({ type: OfferPageActions.resetDatesToMinimumDuration, minimumDuration })
      return
    }

    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch, isFixedFlashLengthOfStayEnabled, minimumDuration])

  useEffect(() => {
    if (footerSelectedDay) {
      setLocalDay(footerSelectedDay)
    }
  }, [footerSelectedDay])

  const onFooterClose = useCallback<React.TransitionEventHandler<HTMLDivElement>>((e) => {
    if (!shouldShowFooter && e.propertyName === 'transition') {
      setLocalDay(undefined)
    }
  }, [shouldShowFooter])

  return <FooterContents
    className={clsx({ visible: shouldShowFooter })}
    onTransitionEnd={onFooterClose}
  >
    {(!canBookNow || !isAnyFlightsActive) && <ModalFooter>
      {useFixedFlashMobileFooter && showClearDatesOnly && <TextButton
        kind="secondary"
        size="large"
        fit="full-width"
        onClick={onResetDates}
      >
        {clearDatesLabel}
      </TextButton>}
      {useFixedFlashMobileFooter && !showClearDatesOnly && <MobileButtonGroup direction="horizontal" gap={8}>
        <TextButton kind="secondary" size="large" fit="flex" onClick={onResetDates}>
          {clearDatesLabel}
        </TextButton>
        {canAddToUserCart && <OfferAddToCartButton
          size="large"
          data-buytype={isAnyFlightsActive ? 'flights' : undefined}
          packageView={selectedPackageView ?? relevantPackages[0]}
        />}
        {canBookNow && <OfferBookButton
          size="large"
          data-buytype={isAnyFlightsActive ? 'flights' : undefined}
          packageView={selectedPackageView ?? relevantPackages[0]}
          onSubmit={onBookNowButtonClick}
        >
          {intl.formatMessage({
            id: 'HW_QFGgLvCYyexDKqDVI',
            defaultMessage: 'Book now',
          })}
        </OfferBookButton>}
        {!canBookNow && !hideContinue && <TextButton
          kind="primary"
          size="large"
          fit="flex"
          type="submit"
          onClick={onContinue}
        >
          {intl.formatMessage({
            id: 'KrDQFb1AzSMgOKMVzk74',
            defaultMessage: 'Continue',
          })}
        </TextButton>}
      </MobileButtonGroup>}
      {!useNewMobileHotelOfferPage && <VerticalSpacer gap={8}>
        {localDay && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <BodyText variant="medium">
            <Group direction="horizontal" verticalAlign="center">
              <span>{intlDateFormatter(localDay.checkIn, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
              <LineArrowRightIcon size="XS" />
              <span>{intlDateFormatter(localDay.checkOut, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
            </Group>
          </BodyText>
          <TextButton kind="tertiary" horizontalOutdent="end" onClick={onResetDates}>
            {resetDatesLabel}
          </TextButton>
        </Group>}
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
          <div>
            {localDay && <OfferCalenderDatePickerFooterPriceDetails
              offer={offer}
              duration={duration}
              selectedDay={localDay}
              isAnyFlightsActive={isAnyFlightsActive}
              isFromPrice={relevantPackages.length > 1}
            />}
          </div>
          {canAddToUserCart && <>
            {isAnyFlightsActive && <OfferSelectPackageButton
              size="large"
              data-buytype="flights"
              packageView={selectedPackageView ?? relevantPackages[0]}
            />}
            {!isAnyFlightsActive && <OfferAddToCartButton
              size="large"
              packageView={selectedPackageView ?? relevantPackages[0]}
            />}
          </>}
          {canBookNow && offer.type === OFFER_TYPE_VILLA && <TextButton
            kind="primary"
            size="large"
            onClick={onContinue}
          >
            {intl.formatMessage({
              id: '3TC2y07y0gyi_2PPfLg5',
              defaultMessage: 'Confirm',
            })}
          </TextButton>}
          {canBookNow && offer.type !== OFFER_TYPE_VILLA && <OfferBookButton
            size="large"
            data-buytype={isAnyFlightsActive ? 'flights' : undefined}
            packageView={selectedPackageView ?? relevantPackages[0]}
            onSubmit={onBookNowButtonClick}
          >
            {intl.formatMessage({
              id: 'HW_QFGgLvCYyexDKqDVI',
              defaultMessage: 'Book now',
            })}
          </OfferBookButton>}
          {!canBookNow && !hideContinue && <TextButton
            kind="primary"
            size="large"
            onClick={onContinue}
          >
            {intl.formatMessage({
              id: 'KrDQFb1AzSMgOKMVzk74',
              defaultMessage: 'Continue',
            })}
          </TextButton>}
        </Group>
      </VerticalSpacer>}
      {useNewMobileHotelOfferPage && !isFixedFlashLengthOfStayEnabled && <MobileButtonGroup direction="horizontal" gap={8}>
        <TextButton kind="tertiary" size="large" fit="flex" onClick={onResetDates}>
          {resetButtonLabel}
        </TextButton>
        {canAddToUserCart && <OfferAddToCartButton
          size="large"
          data-buytype={isAnyFlightsActive ? 'flights' : undefined}
          packageView={selectedPackageView ?? relevantPackages[0]}
        />}
        {canBookNow && <OfferBookButton
          size="large"
          data-buytype={isAnyFlightsActive ? 'flights' : undefined}
          packageView={selectedPackageView ?? relevantPackages[0]}
          onSubmit={onBookNowButtonClick}
        >
          {intl.formatMessage({
            id: 'HW_QFGgLvCYyexDKqDVI',
            defaultMessage: 'Book now',
          })}
        </OfferBookButton>}
        {!canBookNow && !hideContinue && <TextButton
          kind="primary"
          size="large"
          fit="flex"
          type="submit"
          onClick={onContinue}
        >
          {intl.formatMessage({
            id: 'KrDQFb1AzSMgOKMVzk74',
            defaultMessage: 'Continue',
          })}
        </TextButton>}
      </MobileButtonGroup>}
    </ModalFooter>}
    {canBookNow && isAnyFlightsActive && <ModalFooter>
      <TextButton kind="primary" size="large" onClick={onContinue}>
        {intl.formatMessage({
          id: '9YqJDAm8X1KrtENIq_xp',
          defaultMessage: 'Select package',
        })}
      </TextButton>
    </ModalFooter>}
  </FooterContents>
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    offer: state.offer,
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    fixedFlashLengthOfStaySelectedDay: isMultiRoom ? undefined : getSelectedFixedFlashLengthOfStayOfferPageDay(appState, state),
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    duration: state.duration,
    isAnyFlightsActive: state.flights,
    selectedPackageView: getSelectedPackageView(state, appState),
    relevantPackages: isMultiRoom ? getMultiRoomPackagesForFilters(state, appState) : getPackagesForFilters(appState, state),
    bookable: canBookNow(state, appState),
    mobileMultiRoomMode: state.multiRoomMode,
    minimumDuration: getFirstAvailableDuration(getDurationOptions(state, appState)),
  }
}, OfferCalendarSidebarDatePickerFooter)
